import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-azure-wafr-questions-answers",
  templateUrl: "./azure-wafr-questions-answers.component.html",
  styleUrls: [
    "./azure-wafr-questions-answers.component.css",
    "../../azure-wafr-workloads.component.css",
    "../../../../../warquiz/components/questions/questions.component.css",
    "../../../../../warquiz/warquiz.component.css",
  ],
})
export class AzureWafrQuestionsAnswersComponent implements OnChanges {
  @Input() selectedWorkload: any = null;
  @Input() selectedPillar: any = null;
  @Input() pillars: any[] = [];
  @Input() pillarsLoading = false;
  @Output() back = new EventEmitter<void>();
  @Output() openPillar = new EventEmitter<any>();
  @Output() answerSaved = new EventEmitter<any>();
  currentQuestionIndex = 0;
  answersLoading = false;
  answersError: string = null;
  answersQuestions: any[] = [];
  answerSaving = false;
  answerSaveError: string = null;
  showSidebar = true;
  urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  private requestCounter = 0;
  private questionDetailsRequestCounter = 0;
  private answersCache: { [key: string]: any[] } = {};

  constructor(
    private http: HttpClient,
    private apiService: APIService,
    private notifier: NotifierService
  ) {}

  onBack() {
    this.back.emit();
  }

  onSidebarPillarSelect(pillar: any) {
    if (!pillar || this.isSidebarPillarSelected(pillar)) {
      return;
    }
    this.discardUnsavedChanges(this.currentQuestion);
    this.answerSaveError = null;
    this.openPillar.emit(pillar);
  }

  get sidebarPillars(): any[] {
    const rows = Array.isArray(this.pillars) ? this.pillars : [];
    if (rows.length > 0) {
      return rows;
    }
    return this.selectedPillar ? [this.selectedPillar] : [];
  }

  isSidebarPillarSelected(pillar: any): boolean {
    return this.isSamePillar(pillar, this.selectedPillar);
  }

  getPillarAnsweredLabel(pillar: any): string {
    const answeredLabel = this.toText(pillar?.answered_label).trim();
    if (answeredLabel) {
      return answeredLabel;
    }
    const answered = this.toText(pillar?.answered_questions).trim();
    const total = this.toText(pillar?.total_questions).trim();
    if (answered || total) {
      return `${answered || "0"}/${total || "0"}`;
    }
    return "-";
  }

  getPillarDisplayName(pillar: any): string {
    const pillarName = this.toText(pillar?.pillar_name).trim();
    if (pillarName) {
      return pillarName;
    }
    return this.formatPillarName(this.toText(pillar?.pillar));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedPillar"] || changes["selectedWorkload"]) {
      this.currentQuestionIndex = 0;
      this.answerSaveError = null;
      this.loadAnswers();
    }
  }

  get questions(): any[] {
    const rows =
      this.answersQuestions.length > 0
        ? this.answersQuestions
        : Array.isArray(this.selectedPillar?.questions)
          ? this.selectedPillar.questions
          : [];
    return [...rows]
      .map((question: any) => this.ensureEditableFields(question))
      .sort((a: any, b: any) => {
      const aOrder = a?.nav_order !== undefined ? a.nav_order : 9999;
      const bOrder = b?.nav_order !== undefined ? b.nav_order : 9999;
      return aOrder - bOrder;
    });
  }

  get currentQuestion(): any {
    if (this.questions.length === 0) {
      return null;
    }
    return this.questions[this.currentQuestionIndex];
  }

  async prev() {
    if (this.currentQuestionIndex > 0) {
      this.discardUnsavedChanges(this.currentQuestion);
      this.currentQuestionIndex -= 1;
      await this.loadCurrentQuestionAnswer();
    }
  }

  async next() {
    if (this.currentQuestionIndex < this.questions.length - 1) {
      this.discardUnsavedChanges(this.currentQuestion);
      this.currentQuestionIndex += 1;
      await this.loadCurrentQuestionAnswer();
    }
  }

  async changeQuestion(index: number) {
    if (index < 0 || index >= this.questions.length) {
      return;
    }
    this.discardUnsavedChanges(this.currentQuestion);
    this.currentQuestionIndex = index;
    await this.loadCurrentQuestionAnswer();
  }

  isOptionSelected(question: any, option: any): boolean {
    const optionId = this.toText(option?.option_id || option?.id).trim();
    if (!optionId || !question) {
      return false;
    }
    const selectedOptionIds =
      question?.selected_option_ids ||
      question?.selected_choices ||
      question?.answer?.selected_options ||
      [];
    if (Array.isArray(selectedOptionIds)) {
      return selectedOptionIds
        .map((value: any) => this.toText(value).trim())
        .includes(optionId);
    }
    return false;
  }

  isQuestionAnswered(question: any): boolean {
    if (!question) {
      return false;
    }
    // UI rule: answered (green) only when the SAVED state has selected options.
    return this.hasSavedSelectedOptions(question);
  }

  onOptionToggle(question: any, option: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.answerSaving) {
      return;
    }

    question = this.ensureEditableFields(question);
    const optionId = this.toText(option?.option_id || option?.id).trim();
    if (!optionId) {
      return;
    }

    const selectedOptionIds = Array.isArray(question.selected_option_ids)
      ? question.selected_option_ids
          .map((value: any) => this.toText(value).trim())
          .filter((value: string) => !!value)
      : [];

    let nextSelected = [...selectedOptionIds];
    const isNoneOfTheAboveOption = this.isNoneOfTheAbove(option);

    if (isNoneOfTheAboveOption) {
      nextSelected = selectedOptionIds.includes(optionId) ? [] : [optionId];
    } else {
      const noneOfTheAboveOptionId = this.getNoneOfTheAboveOptionId(question);
      if (noneOfTheAboveOptionId) {
        nextSelected = nextSelected.filter((id: string) => id != noneOfTheAboveOptionId);
      }
      if (nextSelected.includes(optionId)) {
        nextSelected = nextSelected.filter((id: string) => id != optionId);
      } else {
        nextSelected.push(optionId);
      }
    }

    question.selected_option_ids = nextSelected;
    const questionType = this.toText(question?.question_type).trim().toLowerCase();
    if (questionType.includes("select")) {
      question.answer_value = nextSelected.length > 0 ? "Yes" : "";
    } else if (!this.normalizeInputValue(question.answer_value)) {
      question.answer_value = nextSelected.length > 0 ? "Yes" : "";
    }

    question.__dirty = true;
    this.answerSaveError = null;
  }

  onQuestionFieldChange(question: any) {
    if (!question) {
      return;
    }
    this.ensureEditableFields(question);
    question.__dirty = true;
    this.answerSaveError = null;
  }

  async onSaveCurrentQuestion() {
    const saved = await this.saveQuestionAnswer(this.currentQuestion, true);
    if (saved) {
      await this.loadCurrentQuestionAnswer();
    }
  }

  private formatDateTime(value: string): string {
    if (!value) {
      return "-";
    }
    const dt = new Date(value);
    if (isNaN(dt.getTime())) {
      return value;
    }
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  private toText(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value == "string") {
      return value;
    }
    if (typeof value == "number" || typeof value == "boolean") {
      return `${value}`;
    }
    if (Array.isArray(value)) {
      return value
        .map((item: any) => this.toText(item))
        .filter((item: string) => !!item)
        .join(", ");
    }
    if (typeof value == "object") {
      const nested =
        this.toText(value?.error_message) ||
        this.toText(value?.message) ||
        this.toText(value?.detail) ||
        this.toText(value?.error);
      if (nested) {
        return nested;
      }
      try {
        return JSON.stringify(value);
      } catch (err) {
        return `${value}`;
      }
    }
    return `${value}`;
  }

  private isSamePillar(a: any, b: any): boolean {
    const aPillar = this.toText(a?.pillar).trim().toLowerCase();
    const bPillar = this.toText(b?.pillar).trim().toLowerCase();
    return !!aPillar && !!bPillar && aPillar == bPillar;
  }

  private formatPillarName(value: string): string {
    const key = this.toText(value).trim();
    if (!key) {
      return "-";
    }
    return key
      .replace(/_/g, " ")
      .replace(/\w\S*/g, (word) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      });
  }

  private getAnswersCacheKey(
    workloadId: string,
    pillarName: string,
    subscriptionId: string
  ): string {
    return `${workloadId}::${pillarName}::${subscriptionId}`;
  }

  private extractApiError(result: any): string {
    return (
      this.toText(result?.error_message) ||
      this.toText(result?.message) ||
      this.toText(result?.detail) ||
      this.toText(result?.error) ||
      this.toText(result)
    );
  }

  private mapAnswerToQuestion(answer: any, index: number): any {
    const definition = answer?.question_definition || {};
    const questionId = this.toText(
      answer?.question_id || definition?.question_id || definition?.id
    ).trim();
    const selectedOptionIds = Array.isArray(answer?.selected_option_ids)
      ? answer.selected_option_ids
          .map((value: any) => this.toText(value).trim())
          .filter((value: string) => !!value)
      : [];

    return {
      ...definition,
      ...answer,
      question_id: questionId,
      question: definition?.question || answer?.question || "-",
      description: definition?.description || "",
      question_type: definition?.question_type || "",
      nav_order:
        definition?.nav_order !== undefined && definition?.nav_order !== null
          ? definition.nav_order
          : index + 1,
      options: Array.isArray(definition?.options) ? definition.options : [],
      selected_option_ids: selectedOptionIds,
      __saved_selected_option_ids: [...selectedOptionIds],
      answer_value: this.toText(answer?.answer),
      __saved_answer_value: this.toText(answer?.answer),
      reason: this.toText(answer?.reason),
      __saved_reason: this.toText(answer?.reason),
      user_notes: this.toText(answer?.user_notes),
      __saved_user_notes: this.toText(answer?.user_notes),
      __dirty: false,
      updated_at_label: this.formatDateTime(answer?.updated_at),
    };
  }

  private mapAnswersToQuestions(answers: any[]): any[] {
    return answers
      .map((answer: any, index: number) => this.mapAnswerToQuestion(answer, index))
      .sort((a: any, b: any) => {
        const aOrder = a?.nav_order !== undefined ? a.nav_order : 9999;
        const bOrder = b?.nav_order !== undefined ? b.nav_order : 9999;
        return aOrder - bOrder;
      });
  }

  private ensureEditableFields(question: any): any {
    if (!question || typeof question !== "object") {
      return question;
    }

    if (!Array.isArray(question.selected_option_ids)) {
      question.selected_option_ids = [];
    }
    question.selected_option_ids = question.selected_option_ids
      .map((value: any) => this.toText(value).trim())
      .filter((value: string) => !!value);

    if (!Array.isArray(question.__saved_selected_option_ids)) {
      question.__saved_selected_option_ids = [...question.selected_option_ids];
    } else {
      question.__saved_selected_option_ids = question.__saved_selected_option_ids
        .map((value: any) => this.toText(value).trim())
        .filter((value: string) => !!value);
    }

    if (question.__saved_answer_value === undefined) {
      question.__saved_answer_value = this.normalizeInputValue(question.answer_value);
    }
    if (question.__saved_reason === undefined) {
      question.__saved_reason = this.normalizeInputValue(question.reason);
    }
    if (question.__saved_user_notes === undefined) {
      question.__saved_user_notes = this.normalizeInputValue(question.user_notes);
    }

    question.answer_value = this.normalizeInputValue(
      question.answer_value !== undefined ? question.answer_value : question.answer
    );
    question.reason = this.normalizeInputValue(question.reason);
    question.user_notes = this.normalizeInputValue(question.user_notes);

    if (question.__dirty === undefined) {
      question.__dirty = false;
    }

    return question;
  }

  private normalizeInputValue(value: any): string {
    const text = this.toText(value).trim();
    if (!text) {
      return "";
    }
    const lowered = text.toLowerCase();
    if (lowered == "-" || lowered == "unanswered") {
      return "";
    }
    return text;
  }

  private getQuestionId(question: any): string {
    return this.toText(
      question?.question_id ||
        question?.id ||
        question?.questionId ||
        question?.question_definition?.question_id ||
        question?.question_definition?.id
    ).trim();
  }

  private isNoneOfTheAbove(option: any): boolean {
    const label = this.toText(option?.label || option?.title).trim().toLowerCase();
    return label == "none of the above." || label == "none of the above" || label == "none of these";
  }

  private getNoneOfTheAboveOptionId(question: any): string {
    const options = Array.isArray(question?.options) ? question.options : [];
    const noneOption = options.find((option: any) => this.isNoneOfTheAbove(option));
    return this.toText(noneOption?.option_id || noneOption?.id).trim();
  }

  private hasSelectedOptions(question: any): boolean {
    if (!question) {
      return false;
    }

    const selectedOptionIds =
      question?.selected_option_ids ||
      question?.selected_choices ||
      question?.answer?.selected_options ||
      [];

    if (Array.isArray(selectedOptionIds)) {
      return selectedOptionIds
        .map((value: any) => this.toText(value).trim())
        .filter((value: string) => !!value).length > 0;
    }

    return false;
  }

  private hasSavedSelectedOptions(question: any): boolean {
    if (!question) {
      return false;
    }

    const savedOptionIds = Array.isArray(question.__saved_selected_option_ids)
      ? question.__saved_selected_option_ids
      : [];

    const savedCount = savedOptionIds
      .map((value: any) => this.toText(value).trim())
      .filter((value: string) => !!value).length > 0;

    if (savedCount) {
      return true;
    }

    if (question.__dirty !== true) {
      return this.hasSelectedOptions(question);
    }

    return false;
  }

  private discardUnsavedChanges(question: any) {
    if (!question || question.__dirty !== true) {
      return;
    }

    question.selected_option_ids = Array.isArray(question.__saved_selected_option_ids)
      ? [...question.__saved_selected_option_ids]
      : [];
    question.answer_value = this.normalizeInputValue(question.__saved_answer_value);
    question.reason = this.normalizeInputValue(question.__saved_reason);
    question.user_notes = this.normalizeInputValue(question.__saved_user_notes);
    question.__dirty = false;
    this.answerSaveError = null;
  }

  private async loadCurrentQuestionAnswer() {
    const question = this.currentQuestion;
    if (!question) {
      return;
    }
    await this.loadQuestionAnswer(question);
  }

  private async loadQuestionAnswer(question: any) {
    const workloadId = this.toText(this.selectedWorkload?.workload_id).trim();
    const pillarName = this.toText(this.selectedPillar?.pillar).trim();
    const subscriptionId = this.toText(
      this.selectedWorkload?.subscription_id || localStorage.getItem("accountId") || ""
    ).trim();
    const questionId = this.getQuestionId(question);

    if (!workloadId || !pillarName || !subscriptionId || !questionId) {
      return;
    }

    const requestId = ++this.questionDetailsRequestCounter;
    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads/` +
      `${encodeURIComponent(workloadId)}/pillars/${encodeURIComponent(
        pillarName
      )}/answers/${encodeURIComponent(questionId)}?subscription_id=` +
      `${encodeURIComponent(subscriptionId)}`;

    try {
      const result: any = await this.apiService.getDataPromis(apiUrl, {}, headers);
      if (requestId != this.questionDetailsRequestCounter) {
        return;
      }

      if (result?.question_id || result?.question_definition) {
        const mapped = this.mapAnswerToQuestion(result, question?.nav_order || 1);
        const rows =
          this.answersQuestions.length > 0
            ? this.answersQuestions
            : Array.isArray(this.selectedPillar?.questions)
              ? this.selectedPillar.questions
              : [];

        const rowIndex = rows.findIndex((row: any) => this.getQuestionId(row) == questionId);
        if (rowIndex > -1) {
          const dirty = rows[rowIndex]?.__dirty === true;
          rows[rowIndex] = { ...rows[rowIndex], ...mapped, __dirty: dirty };
        }

        if (this.getQuestionId(question) == questionId) {
          const dirty = question.__dirty === true;
          Object.assign(question, mapped);
          question.__dirty = dirty;
        }
        return;
      }

      const errorMessage = this.extractApiError(result);
      if (errorMessage) {
        this.answerSaveError = errorMessage;
      }
    } catch (err: any) {
      if (requestId != this.questionDetailsRequestCounter) {
        return;
      }
      const errorMessage =
        this.extractApiError(err?.error || err) || "Unable to load question answer details.";
      this.answerSaveError = errorMessage;
    }
  }

  private applySaveResult(result: any, savedQuestion: any) {
    const questionId = this.getQuestionId(savedQuestion);

    let matchedResult: any = null;
    if (Array.isArray(result?.results) && result.results.length > 0) {
      matchedResult =
        result.results.find((row: any) => this.toText(row?.question_id).trim() == questionId) ||
        result.results[0];
    } else if (result?.question_id || result?.answer !== undefined) {
      matchedResult = result;
    }

    if (!matchedResult) {
      savedQuestion.__dirty = false;
      return;
    }

    const selectedOptionIds = Array.isArray(matchedResult?.selected_option_ids)
      ? matchedResult.selected_option_ids
          .map((value: any) => this.toText(value).trim())
          .filter((value: string) => !!value)
      : Array.isArray(savedQuestion?.selected_option_ids)
        ? savedQuestion.selected_option_ids
            .map((value: any) => this.toText(value).trim())
            .filter((value: string) => !!value)
        : [];

    savedQuestion.selected_option_ids = selectedOptionIds;
    savedQuestion.__saved_selected_option_ids = [...selectedOptionIds];
    savedQuestion.answer = this.toText(matchedResult?.answer);
    savedQuestion.answer_value = this.toText(matchedResult?.answer);
    savedQuestion.__saved_answer_value = this.toText(matchedResult?.answer);
    savedQuestion.reason = this.toText(matchedResult?.reason);
    savedQuestion.__saved_reason = this.toText(matchedResult?.reason);
    savedQuestion.user_notes = this.toText(matchedResult?.user_notes);
    savedQuestion.__saved_user_notes = this.toText(matchedResult?.user_notes);
    savedQuestion.score_obtained = matchedResult?.score_obtained;
    savedQuestion.max_score = matchedResult?.max_score;
    savedQuestion.confidence = matchedResult?.confidence;
    savedQuestion.__dirty = false;

    const rows =
      this.answersQuestions.length > 0
        ? this.answersQuestions
        : Array.isArray(this.selectedPillar?.questions)
          ? this.selectedPillar.questions
          : [];

    const index = rows.findIndex((row: any) => this.getQuestionId(row) == questionId);
    if (index > -1) {
      rows[index] = { ...rows[index], ...savedQuestion };
    }
  }

  private async saveQuestionAnswer(question: any, showSuccessAlert: boolean): Promise<boolean> {
    question = this.ensureEditableFields(question);

    if (!question) {
      return false;
    }

    if (this.answerSaving) {
      return false;
    }

    const workloadId = this.toText(this.selectedWorkload?.workload_id).trim();
    const pillarName = this.toText(this.selectedPillar?.pillar).trim();
    const subscriptionId = this.toText(
      this.selectedWorkload?.subscription_id || localStorage.getItem("accountId") || ""
    ).trim();
    const questionId = this.getQuestionId(question);

    if (!workloadId || !pillarName || !subscriptionId) {
      this.answerSaveError = "Workload, pillar or subscription details are missing.";
      return false;
    }

    if (!questionId) {
      this.answerSaveError = "Question ID is missing.";
      return false;
    }

    const payload: any = {
      selected_option_ids: Array.isArray(question.selected_option_ids)
        ? question.selected_option_ids
        : [],
    };

    const answerText = this.normalizeInputValue(question.answer_value);
    const reasonText = this.normalizeInputValue(question.reason);
    const userNotesText = this.normalizeInputValue(question.user_notes);
    const questionType = this.toText(question?.question_type).trim().toLowerCase();

    if (answerText) {
      payload.answer = answerText;
    } else if (
      questionType.includes("select") &&
      Array.isArray(payload.selected_option_ids) &&
      payload.selected_option_ids.length === 0
    ) {
      payload.answer = "Manual";
    }

    if (reasonText) {
      payload.reason = reasonText;
    }

    if (userNotesText) {
      payload.user_notes = userNotesText;
    }

    const headers: any = {
      Authorization: localStorage.getItem("t") || "",
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads/` +
      `${encodeURIComponent(workloadId)}/pillars/${encodeURIComponent(
        pillarName
      )}/answers/${encodeURIComponent(questionId)}?subscription_id=${encodeURIComponent(
        subscriptionId
      )}`;

    this.answerSaving = true;
    this.answerSaveError = null;
    this.notifier.loading(true);

    try {
      const httpHeaders = new HttpHeaders({
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        Authorization: headers.Authorization,
      });
      const result: any = await this.http
        .patch(apiUrl, payload, { headers: httpHeaders })
        .toPromise();

      if (result?.workload_id) {
        this.applySaveResult(result, question);
        this.answerSaved.emit(result);
        if (showSuccessAlert) {
          this.notifier.alert("Success", "", "Answer saved successfully.", "success", 3000);
        }
        return true;
      }

      this.answerSaveError = this.extractApiError(result) || "Unable to save answer.";
      this.notifier.alert("Info", "", this.answerSaveError, "info", 5000);
      return false;
    } catch (err: any) {
      this.answerSaveError = this.extractApiError(err?.error || err) || "Unable to save answer.";
      this.notifier.alert("Info", "", this.answerSaveError, "info", 5000);
      return false;
    } finally {
      this.answerSaving = false;
      this.notifier.loading(false);
    }
  }

  private async loadAnswers() {
    const workloadId = this.selectedWorkload?.workload_id;
    const pillarName = this.selectedPillar?.pillar;
    const subscriptionId =
      this.toText(
        this.selectedWorkload?.subscription_id ||
        localStorage.getItem("accountId") ||
        ""
      ).trim();

    this.answersError = null;
    this.answersQuestions = [];
    this.answerSaveError = null;

    if (!workloadId || !pillarName) {
      this.answersLoading = false;
      return;
    }

    if (!subscriptionId) {
      this.answersLoading = false;
      this.answersError = "Subscription ID is missing.";
      return;
    }

    const cacheKey = this.getAnswersCacheKey(
      this.toText(workloadId).trim(),
      this.toText(pillarName).trim(),
      subscriptionId
    );
    const hasQuestionDefinitions =
      Array.isArray(this.selectedPillar?.questions) &&
      this.selectedPillar.questions.length > 0;

    if (Array.isArray(this.answersCache[cacheKey])) {
      this.answersQuestions = this.mapAnswersToQuestions(this.answersCache[cacheKey]);
      this.answersLoading = false;
      if (this.answersQuestions.length > 0) {
        await this.loadCurrentQuestionAnswer();
      }
      return;
    }

    const requestId = ++this.requestCounter;
    this.answersLoading = !hasQuestionDefinitions;

    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr-v2/api/v1/azure/wafr/workloads/` +
      `${encodeURIComponent(workloadId)}/pillars/${encodeURIComponent(
        pillarName
      )}/answers?subscription_id=${encodeURIComponent(subscriptionId)}`;

    try {
      const result: any = await this.apiService.getDataPromis(apiUrl, {}, headers);

      if (requestId != this.requestCounter) {
        return;
      }

      if (Array.isArray(result?.answers)) {
        this.answersCache[cacheKey] = result.answers;
        this.answersQuestions = this.mapAnswersToQuestions(result.answers);
        if (this.answersQuestions.length > 0) {
          await this.loadCurrentQuestionAnswer();
        }
      } else {
        this.answersError = this.extractApiError(result) || "Unable to load answers.";
      }
    } catch (err: any) {
      if (requestId != this.requestCounter) {
        return;
      }
      this.answersError = this.extractApiError(err) || "Unable to load answers.";
    } finally {
      if (requestId == this.requestCounter) {
        this.answersLoading = false;
      }
    }
  }
}
